<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import { Column, DataTable } from 'primevue';
    import ScheduleCoachingTableCell from './ScheduleCoachingTableCell.vue';
    import TablePaginator from '../../dataTable/TablePaginator.vue';
    import SortableColumnHeader from '../../dataTable/SortableColumnHeader.vue';
    import { useScheduleCoachingFormTableStore } from '@/js/stores/schedule-coaching-form-table';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { useQueryString } from '@/js/composables/useQueryString';
    import { computed, ref, watch } from 'vue';
    import { PageState } from 'primevue';
    import {
        useGetScheduleCoachingForms,
        useMutateDeleteScheduleCoachingJob,
    } from '@/js/services/api/scheduleCoachingForm/scheduleCoachingFormQueries';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { IScheduledCoachJob } from '@/js/services/api/scheduleCoachingForm/types';
    import { useScheduleCoaching } from '@/js/stores/scheduleCoaching';
    import TableLoadingOverlay from '../../dataTable/TableLoadingOverlay.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import BottomScrollFade from '../../dataTable/BottomScrollFade.vue';

    const { hasPermission } = usePermissions();

    const canScheduleCoaching = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ScheduleCoaching
    );

    export type ScheduleCoachingFormTableColumn = {
        field: 'title' | 'createdAt' | 'createdByUsername' | 'actions';
        name: string;
        sortable?: boolean;
    };

    const allColumns: ScheduleCoachingFormTableColumn[] = [
        { field: 'title', name: __('schedule-coaching-form-table-column-name') },
        {
            field: 'createdAt',
            name: __('schedule-coaching-form-table-column-created-at'),
        },
        { field: 'createdByUsername', name: __('schedule-coaching-form-table-column-created-by') },
        { field: 'actions', name: __('schedule-coaching-form-table-column-actions') },
    ];

    const columns = computed(() =>
        canScheduleCoaching.value ? allColumns : allColumns.filter((col) => col.field !== 'actions')
    );

    const scheduleCoachingFormTableStore = useScheduleCoachingFormTableStore();

    const pageSize = useTablePageSize('schedule-coaching-forms');
    const querystring = useQueryString();
    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;
        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const { data, isFetching } = useGetScheduleCoachingForms();

    const scheduleCoachingForm = useScheduleCoaching();

    const { mutateAsync: deleteAsync } = useMutateDeleteScheduleCoachingJob();

    const deleteConfirmation = (coachJob: IScheduledCoachJob) => ({
        title: __('schedule-coaching-form-delete-title'),
        message: __('schedule-coaching-form-delete-description'),
        confirmText: __('schedule-coaching-form-delete-confirm'),
        callback: async () => await deleteAsync({ coachJobId: coachJob.id }),
    });

    const confirm = useConfirmationDialog();

    const handleDelete = async (coachJob: IScheduledCoachJob) => {
        const options = deleteConfirmation(coachJob);
        await confirm.openDialog(options);
    };

    const handleEdit = (coachJob: IScheduledCoachJob) => {
        scheduleCoachingForm.openEdit(coachJob);
    };

    watch(
        () => data.value,
        (newVal) => {
            if (newVal) {
                scheduleCoachingFormTableStore.setForms(newVal.scheduledCoachJobs);
            }
        },
        { immediate: true }
    );

    const tableRef = ref<HTMLElement | null>(null);
</script>
<template>
    <div ref="tableRef" class="echo-table-wrapper echo-table-wrapper--fill">
        <DataTable
            class="p-datatable-no-top-border row-padding"
            :value="scheduleCoachingFormTableStore.filteredScheduledForms"
            data-key="id"
            paginator
            :loading="isFetching"
            :total-records="scheduleCoachingFormTableStore.filteredScheduledForms.length"
            :rows="pageSize"
            :page="scheduleCoachingFormTableStore.page"
            :first="scheduleCoachingFormTableStore.page * pageSize"
            :scrollable="true"
            scroll-height="flex"
        >
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <template #empty>
                <div class="h-100 flex items-center justify-center">
                    <h2 v-if="!isFetching" class="text-center">
                        {{ __('schedule-coaching-form-table-empty-message') }}
                    </h2>
                </div>
            </template>
            <Column v-for="col in columns" :key="col.field" :field="col.field">
                <template #header>
                    <SortableColumnHeader
                        :name="col.name"
                        :sortable="col.sortable"
                        @sort="(dir) => scheduleCoachingFormTableStore.changeSort(col.field, dir)"
                    />
                </template>
                <template #body="{ data: rowData }: { data: IScheduledCoachJob }">
                    <ScheduleCoachingTableCell
                        :column="col.field"
                        :data="rowData"
                        :can-edit="rowData.canManage"
                        :can-delete="rowData.canManage"
                        @delete="handleDelete"
                        @edit="handleEdit"
                    />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <BottomScrollFade :container-ref="tableRef" />
    </div>
</template>
